import React from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Stack,
  Typography,
  Paper,
  FormHelperText,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { selectedStreamState, authState } from "../../Recoil/RecoilState";
import axios from "axios";
import { baseURL, streamTypesArr } from "../../utils/StaticVariables";
import { useRecoilValue } from "recoil";
import LoadBtn from "../../Components/Reusable/LoadBtn";
import { useNavigate } from "react-router-dom";
import CustomBtn from "../../Components/Reusable/CustomBtn";
import { useSnackbar } from "notistack";

export default function UpdateStreams({ loading = false, onBack }) {
  const selectedData = useRecoilValue(selectedStreamState);
  const navigate = useNavigate();
  const { token } = useRecoilValue(authState);
  const { enqueueSnackbar } = useSnackbar();

  const validationSchema = yup.object({
    streams: yup.array().of(
      yup.object({
        id: yup.string().required(),
        name: yup
          .string()
          .required("Name is required")
          .min(2, "Name must be at least 2 characters"),
        path: yup.string().required("Path is required"),
        type: yup
          .string()
          .oneOf(streamTypesArr, "Invalid type")
          .required("Type is required"),
        area: yup.string().required("Area is required"),
        building: yup.string().required("Building is required"),
        count_threshold_greater: yup
          .number()
          .min(0, "Must be greater than or equal to 0")
          .required("Required"),
        count_threshold_less: yup
          .number()
          .min(0, "Must be greater than or equal to 0")
          .required("Required"),
        floor_level: yup.string().required("Floor level is required"),
        latitude: yup.number().required("Latitude is required"),
        longitude: yup.number().required("Longitude is required"),
        location: yup.string().required("Location is required"),
        status: yup.string().oneOf(["active", "inactive"]).required(),
        zone: yup.string().required("Zone is required"),
      })
    ),
  });

  const formik = useFormik({
    initialValues: {
      streams: selectedData.map((item) => ({
        ...item,
        area: item.area || "",
        building: item.building || "",
        count_threshold_greater: item.count_threshold_greater ?? 0,
        count_threshold_less: item.count_threshold_less ?? 0,
        floor_level: item.floor_level || "",
        latitude: item.latitude ?? 0,
        longitude: item.longitude ?? 0,
        location: item.location || "",
        status: item.status || "active",
        zone: item.zone || "",
      })),
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const cleanedData = values.streams.map((stream) => ({
        id: stream.id,
        name: stream.name,
        path: stream.path,
        type: stream.type,
        area: stream.area,
        building: stream.building,
        count_threshold_greater: stream.count_threshold_greater,
        count_threshold_less: stream.count_threshold_less,
        floor_level: stream.floor_level,
        latitude: stream.latitude,
        longitude: stream.longitude,
        location: stream.location,
        status: stream.status,
        zone: stream.zone,
        is_streaming: stream.is_streaming,
      }));

      try {
        await axios.put(`${baseURL}source`, cleanedData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        enqueueSnackbar("You update your stream Successful", {
          variant: "success",
        });
      } catch (error) {
        enqueueSnackbar(
          "Error updating stream:" + (error?.message || "something went wrong"),
          { variant: "error" }
        );
      }
    },
  });

  const handleReset = () => {
    formik.resetForm();
  };

  const renderField = (label, name, index, type = "text") => (
    <div>
      <Typography variant="h6" sx={textStyle}>
        {label}:
      </Typography>
      <TextField
        fullWidth
        type={type}
        name={`streams.${index}.${name}`}
        placeholder={`Enter ${label}`}
        value={formik.values.streams[index]?.[name] || ""}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={Boolean(
          formik.touched.streams?.[index]?.[name] &&
            formik.errors.streams?.[index]?.[name]
        )}
        helperText={
          formik.touched.streams?.[index]?.[name] &&
          formik.errors.streams?.[index]?.[name]
        }
      />
    </div>
  );

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{ mx: "auto", p: 3 }}
      noValidate
    >
      {formik.values.streams.length > 0 ? (
        formik.values.streams.map((stream, index) => (
          <Paper key={stream.id || index} elevation={1} sx={{ mb: 3, p: 3 }}>
            <Stack spacing={3}>
              {renderField("Name", "name", index)}
              {renderField("Source", "path", index)}
              <div>
                <Typography variant="h6" sx={textStyle}>
                  Type:
                </Typography>
                <FormControl
                  fullWidth
                  error={Boolean(
                    formik.touched.streams?.[index]?.type &&
                      formik.errors.streams?.[index]?.type
                  )}
                >
                  <Select
                    name={`streams.${index}.type`}
                    value={formik.values.streams[index]?.type || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {streamTypesArr.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.streams?.[index]?.type &&
                    formik.errors.streams?.[index]?.type && (
                      <FormHelperText>
                        {formik.errors.streams[index].type}
                      </FormHelperText>
                    )}
                </FormControl>
              </div>
              {renderField("Area", "area", index)}
              {renderField("Building", "building", index)}
              {renderField("Count Threshold Greater", "count_threshold_greater", index, "number")}
              {renderField("Count Threshold Less", "count_threshold_less", index, "number")}
              {renderField("Floor Level", "floor_level", index)}
              {renderField("Latitude", "latitude", index, "number")}
              {renderField("Longitude", "longitude", index, "number")}
              {renderField("Location", "location", index)}
              {renderField("Zone", "zone", index)}
              <div>
                <Typography variant="h6" sx={textStyle}>
                  Status:
                </Typography>
                <FormControl
                  fullWidth
                  error={Boolean(
                    formik.touched.streams?.[index]?.status &&
                      formik.errors.streams?.[index]?.status
                  )}
                >
                  <Select
                    name={`streams.${index}.status`}
                    value={formik.values.streams[index]?.status || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                  {formik.touched.streams?.[index]?.status &&
                    formik.errors.streams?.[index]?.status && (
                      <FormHelperText>
                        {formik.errors.streams[index].status}
                      </FormHelperText>
                    )}
                </FormControl>
              </div>
            </Stack>
          </Paper>
        ))
      ) : (
        <Typography
          variant="body1"
          color="text.secondary"
          align="center"
          sx={{ py: 4 }}
        >
          No streams available to edit.
        </Typography>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 4 }}
      >
        <CustomBtn isLined handle={() => navigate("/streams")}>
          Back
        </CustomBtn>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!formik.dirty}
          >
            Clear
          </Button>

          <LoadBtn loading={loading} />
        </Stack>
      </Stack>
    </Box>
  );
}

const textStyle = {
  fontSize: ".9rem",
  fontWeight: 600,
  textTransform: "capitalize",
};
